/**
 * useObstructionState - Consolidated obstruction state management
 *
 * Manages all obstruction-related states including:
 * - Obstruction data per polygon
 * - Drawing modes (rectangle, circle, polygon)
 * - Selection and editing states
 * - Preview states for drawing
 */

import { useState, useCallback } from 'react';

export type ObstructionDrawMode = 'rectangle' | 'circle' | 'polygon';

export interface ObstructionSelection {
  polygonId: number;
  index: number;
}

export interface ObstructionState {
  // Core obstruction data - map of polygonId to array of obstruction polygons
  obstructions: { [polygonId: number]: [number, number][][] };

  // Drawing state
  isDrawingObstruction: boolean;
  obstructionDrawMode: ObstructionDrawMode;
  dragPreviewRect: [number, number][] | null;
  obstructionDrawingVertices: [number, number][];

  // Circle drawing state
  circleObstructionCenter: [number, number] | null;
  circleObstructionRadius: number;

  // Selection/editing state
  selectedObstruction: ObstructionSelection | null;
  editingObstruction: ObstructionSelection | null;
  draggingObstruction: ObstructionSelection | null;
}

export interface ObstructionStateActions {
  // Core data setters
  setObstructions: React.Dispatch<React.SetStateAction<{ [polygonId: number]: [number, number][][] }>>;

  // Drawing mode setters
  setIsDrawingObstruction: (value: boolean) => void;
  setObstructionDrawMode: (mode: ObstructionDrawMode) => void;
  setDragPreviewRect: (rect: [number, number][] | null) => void;
  setObstructionDrawingVertices: React.Dispatch<React.SetStateAction<[number, number][]>>;

  // Circle drawing setters
  setCircleObstructionCenter: (center: [number, number] | null) => void;
  setCircleObstructionRadius: (radius: number) => void;

  // Selection/editing setters
  setSelectedObstruction: (selection: ObstructionSelection | null) => void;
  setEditingObstruction: (selection: ObstructionSelection | null) => void;
  setDraggingObstruction: (selection: ObstructionSelection | null) => void;

  // Convenience methods
  addObstruction: (polygonId: number, coords: [number, number][]) => void;
  removeObstruction: (polygonId: number, index: number) => void;
  updateObstruction: (polygonId: number, index: number, coords: [number, number][]) => void;
  clearObstructionsForPolygon: (polygonId: number) => void;
  startDrawing: (mode: ObstructionDrawMode) => void;
  stopDrawing: () => void;
  clearSelection: () => void;
}

export function useObstructionState(): ObstructionState & ObstructionStateActions {
  // Core obstruction data
  const [obstructions, setObstructions] = useState<{ [polygonId: number]: [number, number][][] }>({});

  // Drawing state
  const [isDrawingObstruction, setIsDrawingObstruction] = useState(false);
  const [obstructionDrawMode, setObstructionDrawMode] = useState<ObstructionDrawMode>('rectangle');
  const [dragPreviewRect, setDragPreviewRect] = useState<[number, number][] | null>(null);
  const [obstructionDrawingVertices, setObstructionDrawingVertices] = useState<[number, number][]>([]);

  // Circle drawing state
  const [circleObstructionCenter, setCircleObstructionCenter] = useState<[number, number] | null>(null);
  const [circleObstructionRadius, setCircleObstructionRadius] = useState(0);

  // Selection/editing state
  const [selectedObstruction, setSelectedObstruction] = useState<ObstructionSelection | null>(null);
  const [editingObstruction, setEditingObstruction] = useState<ObstructionSelection | null>(null);
  const [draggingObstruction, setDraggingObstruction] = useState<ObstructionSelection | null>(null);

  // Convenience methods
  const addObstruction = useCallback((polygonId: number, coords: [number, number][]) => {
    setObstructions(prev => ({
      ...prev,
      [polygonId]: [...(prev[polygonId] || []), coords],
    }));
  }, []);

  const removeObstruction = useCallback((polygonId: number, index: number) => {
    setObstructions(prev => ({
      ...prev,
      [polygonId]: (prev[polygonId] || []).filter((_, i) => i !== index),
    }));
  }, []);

  const updateObstruction = useCallback((polygonId: number, index: number, coords: [number, number][]) => {
    setObstructions(prev => ({
      ...prev,
      [polygonId]: (prev[polygonId] || []).map((obs, i) => i === index ? coords : obs),
    }));
  }, []);

  const clearObstructionsForPolygon = useCallback((polygonId: number) => {
    setObstructions(prev => {
      const newObstructions = { ...prev };
      delete newObstructions[polygonId];
      return newObstructions;
    });
  }, []);

  const startDrawing = useCallback((mode: ObstructionDrawMode) => {
    setObstructionDrawMode(mode);
    setIsDrawingObstruction(true);
    setDragPreviewRect(null);
    setObstructionDrawingVertices([]);
    setCircleObstructionCenter(null);
    setCircleObstructionRadius(0);
  }, []);

  const stopDrawing = useCallback(() => {
    setIsDrawingObstruction(false);
    setDragPreviewRect(null);
    setObstructionDrawingVertices([]);
    setCircleObstructionCenter(null);
    setCircleObstructionRadius(0);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedObstruction(null);
    setEditingObstruction(null);
    setDraggingObstruction(null);
  }, []);

  return {
    // State
    obstructions,
    isDrawingObstruction,
    obstructionDrawMode,
    dragPreviewRect,
    obstructionDrawingVertices,
    circleObstructionCenter,
    circleObstructionRadius,
    selectedObstruction,
    editingObstruction,
    draggingObstruction,

    // Setters
    setObstructions,
    setIsDrawingObstruction,
    setObstructionDrawMode,
    setDragPreviewRect,
    setObstructionDrawingVertices,
    setCircleObstructionCenter,
    setCircleObstructionRadius,
    setSelectedObstruction,
    setEditingObstruction,
    setDraggingObstruction,

    // Convenience methods
    addObstruction,
    removeObstruction,
    updateObstruction,
    clearObstructionsForPolygon,
    startDrawing,
    stopDrawing,
    clearSelection,
  };
}

export default useObstructionState;
